/*
 * Copyright © 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {LocationStrategy} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject} from '@angular/core';
import {ActivatedRoute, Event, NavigationEnd, NavigationExtras, Router, RouterOutlet} from '@angular/router';

import {LogFactory} from './core/logging/log-factory';
import {DataInitializerService} from './data-store/data-initializer.service';
import {PdfGeneratorService} from './printing/pdf/pdf-generator/pdf-generator.service';

// keep these lines to have translated date formatting!
import 'moment/locale/de-ch.js'
import 'moment/locale/it-ch.js'
import 'moment/locale/fr-ch.js'
import {ToolbarRootComponent} from './toolbar/toolbar-root/toolbar-root.component';

const LOG = LogFactory.createLog('AppComponent');

@Component({
    selector: 'elohn-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [ToolbarRootComponent, RouterOutlet]
})
export class AppComponent {

    public readonly locationStrategy = inject(LocationStrategy);
    private readonly initializer = inject(DataInitializerService);
    private readonly cd = inject(ChangeDetectorRef);
    public readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly pdfGeneratorService = inject(PdfGeneratorService);

    public persisted = 'NOT_INIT';

    constructor() {
        LOG.info('eLohn online started');

        // Wird vom bwipjs font loader gebraucht (siehe: browser-fonts.js)
        // document.baseURI ist eine URL bis einschl. Root-Context (z.B..: http://localhost:8080/de/)
        // Wozu brauchts das? Ohne gibts einen wuesten 404 in der console
        // Siehe: assets/bwipjs-fonts/fnt0-desc.js
        const baseHref = this.locationStrategy.getBaseHref();
        this.bwipJsHack(baseHref);

        this.checkRouteToGoToLastPage();
    }

    private bwipJsHack(baseHref: string): void {
        window.process = window.process ?? {};
        window.process.env = (window.process.env ?? {});
        window.process.env.PUBLIC_URL = `${baseHref}assets`;
        LOG.debug('process env', window.process);
    }

    clearLocationRedirectData(): void {
        localStorage.removeItem('locationPathname');
        localStorage.removeItem('lastRoute');
        localStorage.removeItem('queryParamsRoute');
    }

    checkRouteToGoToLastPage(): void {
        if (location.pathname.includes('printing-development')) {
            LOG.warn('Printing-Development mode: do not restore/remember navigation');
            return;
        }

        if (localStorage.getItem('lastRoute')) {

            const locationPathname = localStorage.getItem('locationPathname') || location.pathname;

            if (locationPathname !== location.pathname) {
                location.pathname = locationPathname;
            }

            const extras: NavigationExtras = {};
            extras.queryParams = JSON.parse(localStorage.getItem('queryParamsRoute') || '{}');
            this.router.navigate([localStorage.getItem('lastRoute')], extras)
                .then(() => this.initLastRouteSubscription())
                .catch((err: unknown) => LOG.error(err));
        } else {
            this.initLastRouteSubscription();
        }
    }

    redirectExclusions(inputUrl: string): boolean {
        const exclusions: ((url: string) => boolean) [] = [];

        exclusions.push((url: string) => url.endsWith('/preview'));

        return exclusions.some(fn => fn(inputUrl));
    }

    initLastRouteSubscription(): void {
        this.router.events.subscribe({
            next: (event: Event) => {
                if (event instanceof NavigationEnd) {
                    try {

                        const urlWithoutParams = this.router.parseUrl(event.url)
                            .root.children.primary.segments.map(it => it.path).join('/');

                        const excluded = this.redirectExclusions(urlWithoutParams);

                        if (!excluded) {
                            localStorage.setItem('locationPathname', location.pathname);
                            localStorage.setItem('lastRoute', urlWithoutParams);
                            localStorage.setItem('queryParamsRoute', JSON.stringify(this.route.snapshot.queryParams));
                        }

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (_) {
                        // console.log(error);
                        //We are in the root
                        this.clearLocationRedirectData();
                    }
                }
            },
            error: (err: unknown) => LOG.error(err),
        });
    }

}
